import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Copy, KeyRound, Shield, Trash2 } from 'lucide-react';
import Modal from '../components/Modal';
import { apiClient } from '../apiClient';
import { PUBLIC_API_BASE } from '../config';

const SCOPES = [
  { value: 'read:templates', label: 'Read prompt templates', description: 'Fetch the latest prompt template with variables intact.' },
  { value: 'read:outputs', label: 'Read saved/completed prompts', description: 'Fetch saved outputs and rendered prompts.' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

function ApiPage() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState(['read:templates']);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get('/api/v1/keys');
      setKeys(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Unable to load API keys.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const created = await apiClient.post('/api/v1/keys', { name: name.trim(), scopes: selectedScopes });
      setCreatedSecret(created.secret);
      setName('');
      setSelectedScopes(['read:templates']);
      setCreateOpen(false);
      await loadKeys();
    } catch (err) {
      setError(err.message || 'Unable to create API key.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (keyId) => {
    setBusy(true);
    setError('');
    try {
      await apiClient.post(`/api/v1/keys/${keyId}/revoke`);
      await loadKeys();
    } catch (err) {
      setError(err.message || 'Unable to revoke key.');
    } finally {
      setBusy(false);
    }
  };

  const baseUrl = useMemo(() => PUBLIC_API_BASE.replace(/\/$/, ''), []);

  return (
    <div className="min-h-screen projects-page">
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1 className="page-title">API Access</h1>
            <p className="page-subtitle">Create API keys to fetch live templates and saved prompt outputs.</p>
          </div>
          <button className="btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4" /> Create API Key
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {createdSecret && (
        <div className="card-glass mb-6">
          <h2 className="text-lg font-semibold mb-2">Your new key (shown once)</h2>
          <div className="input-glass flex items-center justify-between gap-3">
            <span className="text-sm break-all">{createdSecret}</span>
            <button
              className="icon-btn"
              onClick={() => navigator.clipboard.writeText(createdSecret)}
              data-tooltip="Copy"
              aria-label="Copy"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-2">Copy this now. You won’t be able to view it again.</p>
        </div>
      )}

      <div className="card-glass mb-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-4 h-4" />
          <h2 className="text-lg font-semibold">Active Keys</h2>
        </div>
        {loading ? (
          <div className="spinner"></div>
        ) : keys.length === 0 ? (
          <div className="text-gray-400">No API keys yet.</div>
        ) : (
          <div className="space-y-3">
            {keys.map((key) => (
              <div key={key.key_id} className="trash-row">
                <div className="trash-row-main">
                  <div className="trash-row-title">
                    {key.name}
                    {key.revoked_at && <span className="badge ml-2">revoked</span>}
                  </div>
                  <div className="trash-row-meta">
                    {key.key_prefix}…{key.key_last4} • created {formatDate(key.created_at)} • last used {formatDate(key.last_used_at)}
                  </div>
                  <div className="trash-row-meta">
                    Scopes: {(key.scopes || []).join(', ') || 'none'}
                  </div>
                </div>
                {!key.revoked_at && (
                  <button className="btn-secondary danger btn-compact" onClick={() => handleRevoke(key.key_id)} disabled={busy}>
                    <Trash2 className="w-4 h-4" /> Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-glass">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4" />
          <h2 className="text-lg font-semibold">Usage</h2>
        </div>
        <p className="text-sm text-gray-400 mb-3">Send your API key as a Bearer token or X-API-Key header.</p>
        <div className="input-glass text-xs">
          <div>Base URL: {baseUrl}</div>
          <div className="mt-2">GET {baseUrl}/api/v1/hosting/prompts/{'{prompt_id}'}/template</div>
          <div>GET {baseUrl}/api/v1/hosting/prompts/{'{prompt_id}'}/outputs?limit=20&amp;offset=0</div>
          <div>GET {baseUrl}/api/v1/hosting/outputs/{'{output_id}'}</div>
        </div>
      </div>

      <Modal
        open={createOpen}
        title="Create API Key"
        onClose={() => setCreateOpen(false)}
        footer={(
          <div className="modal-actions">
            <button className="btn-secondary" onClick={() => setCreateOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={handleCreate} disabled={busy}>Create</button>
          </div>
        )}
      >
        <div className="modal-field">
          <label>Key Name</label>
          <input className="input-glass" value={name} onChange={(e) => setName(e.target.value)} placeholder="Automation Key" />
        </div>
        <div className="modal-field">
          <label>Permissions</label>
          <div className="space-y-2">
            {SCOPES.map((scope) => (
              <label key={scope.value} className="flex items-start gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(scope.value)}
                  onChange={(e) => {
                    setSelectedScopes((prev) => e.target.checked
                      ? [...prev, scope.value]
                      : prev.filter((item) => item !== scope.value));
                  }}
                />
                <span>
                  <div className="font-medium">{scope.label}</div>
                  <div className="text-xs text-gray-500">{scope.description}</div>
                </span>
              </label>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ApiPage;
